// Expense (Session ATE, engineer expenses and mileage): the client half. Mirrors
// server/src/modules/expenses/expenses.ts.
//
// An engineer's own claims (own submission screen, or from a job screen when job-linked); office/admin
// review, approve or reject; job-linked APPROVED claims feed that job's profitability figure
// (ServiceRecordPanel's "Expenses" stat); general (no job) APPROVED claims are reported separately, as
// a monthly total on the office Reporting page and the Expenses list here -- never folded into one
// job's margin.
import { apiRequest } from "./api";

export const EXPENSE_TYPES = ["mileage", "materials", "other"] as const;
export type ExpenseType = (typeof EXPENSE_TYPES)[number];

export const EXPENSE_TYPE_LABEL: Record<ExpenseType, string> = {
  mileage: "Mileage",
  materials: "Materials",
  other: "Other",
};

export const EXPENSE_STATUSES = ["submitted", "approved", "rejected"] as const;
export type ExpenseStatus = (typeof EXPENSE_STATUSES)[number];

// HMRC's Approved Mileage Allowance Payment rate for a car or van, first 10,000 business miles in a
// tax year. Mirrors the server constant (server/src/modules/expenses/expenses.ts,
// MILEAGE_RATE_PENCE_PER_MILE) so the PWA can show the expected amount before submitting; the server
// always recomputes it, so this is display-only and never trusted for the actual charge.
export const MILEAGE_RATE_PENCE_PER_MILE = 45;

export interface Expense {
  id: string;
  engineerId: string;
  engineerName: string;
  jobId: string | null;
  jobLabel: string | null;
  type: ExpenseType;
  date: string;
  description: string;
  milesClaimed: number | null;
  mileageRatePencePerMile: number | null;
  amountPence: number;
  receiptUrl: string | null;
  status: ExpenseStatus;
  submittedAt: string;
  decidedAt: string | null;
  decidedByName: string | null;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ExpenseMileageInput {
  type: "mileage";
  engineerId?: string;
  jobId?: string | null;
  date: string;
  description: string;
  milesClaimed: number;
}

export interface ExpenseAmountInput {
  type: "materials" | "other";
  engineerId?: string;
  jobId?: string | null;
  date: string;
  description: string;
  amountPence: number;
}

export type ExpenseInput = ExpenseMileageInput | ExpenseAmountInput;

export const expensesApi = {
  // Always read fresh (noCache): the list feeds both the office review page and the engineer's own.
  list: (query: { engineerId?: string; jobId?: string; status?: ExpenseStatus; from?: string; to?: string } = {}): Promise<{ expenses: Expense[] }> => {
    const qs = new URLSearchParams();
    if (query.engineerId) qs.set("engineerId", query.engineerId);
    if (query.jobId) qs.set("jobId", query.jobId);
    if (query.status) qs.set("status", query.status);
    if (query.from) qs.set("from", query.from);
    if (query.to) qs.set("to", query.to);
    const suffix = qs.toString();
    return apiRequest<{ expenses: Expense[] }>(`/expenses${suffix ? `?${suffix}` : ""}`, { noCache: true });
  },
  get: (id: string) => apiRequest<Expense>(`/expenses/${encodeURIComponent(id)}`, { noCache: true }),
  create: (body: ExpenseInput) => apiRequest<Expense>("/expenses", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: Partial<{ jobId: string | null; date: string; description: string; amountPence: number; milesClaimed: number }>) =>
    apiRequest<Expense>(`/expenses/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
  uploadReceipt: (id: string, blob: Blob, filename: string) => {
    const fd = new FormData();
    fd.append("receipt", blob, filename);
    return apiRequest<Expense>(`/expenses/${encodeURIComponent(id)}/receipt`, { method: "POST", body: fd });
  },
  approve: (id: string) => apiRequest<Expense>(`/expenses/${encodeURIComponent(id)}/approve`, { method: "POST" }),
  reject: (id: string, reason?: string) =>
    apiRequest<Expense>(`/expenses/${encodeURIComponent(id)}/reject`, { method: "POST", body: JSON.stringify({ reason }) }),
  remove: (id: string) => apiRequest<{ ok: true; deleted: true }>(`/expenses/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

export function estimatedMileageAmountPence(miles: number): number {
  return Math.round(miles * MILEAGE_RATE_PENCE_PER_MILE);
}
